import React from "react";
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Typography,
    Breadcrumbs,
    Link,
    Button,
} from "@mui/material";
import Pagination from "@mui/material/Pagination";
import { useLocation } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import CircularProgress from '@mui/material/CircularProgress';

const TableComponent = ({
    FiltersJsx,
    columns,
    rows,
    title,
    titleMaster,
    handleHomeClick,
    handleLocationclick,
    isLoading,
    pagination,
    onPageChange,
    onRowsPerPageChange,
}) => {
    const location = useLocation();
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(15);

    const handleChangePage = (newPage) => {
        setPage(newPage);
        onPageChange(newPage + 1);
    };

    const handleChangeRowsPerPage = (event) => {
        const newRowsPerPage = parseInt(event.target.value, 10);
        setRowsPerPage(newRowsPerPage);
        setPage(0);
        onRowsPerPageChange(newRowsPerPage);
    };

    const renderCell = (value, columnId, highlightRow = false) => {
        if (columnId === 'Total') {
            let bgColor = '';
            let textColor = '#000';

            switch (value) {
                case 'Total Bed':
                    bgColor = '#d1e7dd';
                    textColor = '#0f5132';
                    break;
                case 'Extra Bed':
                    bgColor = '#fff3cd';
                    textColor = '#664d03';
                    break;
                case 'BedToSale':
                    bgColor = '#cff4fc';
                    textColor = '#055160';
                    break;
                case 'Booked ':
                    bgColor = '#f8d7da';
                    textColor = '#842029';
                    break;
                case 'Occupied ':
                    bgColor = '#e2e3e5';
                    textColor = '#41464b';
                    break;
                case 'Balance':
                    bgColor = '#e7cffe';
                    textColor = '#4a0072';
                    break;
                default:
                    bgColor = '#f5f5f5';
                    textColor = '#000';
                    break;
            }

            return (
                <div style={{
                    backgroundColor: highlightRow ? 'transparent' : bgColor,  // <-- here
                    color: textColor,
                    textAlign: 'center',
                    fontWeight: 'bold',
                    padding: '4px',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'start'
                }}>
                    {value}
                </div>
            );
        }

        if (typeof value === 'string') {
            return (
                <div style={{
                    textAlign: 'center',
                    fontWeight: 'bold',
                    padding: '4px',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'start',
                    backgroundColor: highlightRow ? 'transparent' : undefined, // <-- here
                }}>
                    {value}
                </div>
            );
        }

        const numValue = parseFloat(value) || 0;
        let backgroundColor = '';
        let textColor = '#000';

        if (numValue === 0) {
            backgroundColor = '#ffebee';
            textColor = '#c62828';
        } else if (numValue <= 2) {
            backgroundColor = '#fff3e0';
            textColor = '#ef6c00';
        } else if (numValue <= 5) {
            backgroundColor = '#f3e5f5';
            textColor = '#2c1fa2ff';
        } else {
            backgroundColor = '#e8f5e8';
            textColor = '#060a06ff';
        }

        return (
            <div style={{
                backgroundColor: highlightRow ? 'transparent' : backgroundColor, // <-- here
                color: textColor,
                textAlign: 'center',
                fontWeight: 'bold',
                padding: '4px',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                {numValue}
            </div>
        );
    };

    const paginatedRows = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return (
        <Box
            sx={{
                width: '100%',
                height: '90vh',
                padding: '10px',
                backgroundColor: 'rgb(249, 249, 249)',
                position: 'relative'
            }}
        >
            <ToastContainer />
            <Box
                sx={{
                    backgroundColor: "#ffffff",
                    padding: "5px 22px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
                    borderRadius: "12px",
                    margin: "10px auto",
                    maxWidth: "95%",
                }}
            >
                <Breadcrumbs
                    aria-label="breadcrumb"
                    separator="›"
                    sx={{ fontSize: "16px" }}
                >
                    <Link
                        onClick={handleHomeClick}
                        underline="hover"
                        color="#635bff"
                        sx={{ cursor: "pointer" }}
                    >
                        Home
                    </Link>
                    <Typography
                        color="#635bff"
                        onClick={handleLocationclick}
                        sx={{ cursor: "pointer", fontWeight: "bold" }}
                    >
                        {title}
                    </Typography>
                    <Typography color="text.primary" sx={{ fontWeight: "bold" }}>
                        {titleMaster}
                    </Typography>
                </Breadcrumbs>
                <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    {FiltersJsx}
                </Box>
            </Box>

            <Paper
                sx={{
                    width: "95%",
                    overflow: "hidden",
                    margin: "10px auto",
                    backgroundColor: "#ffffff",
                    borderRadius: "12px",
                    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
                    padding: "15px",
                    position: "relative",
                }}
            >
                <TableContainer sx={{ maxHeight: 480 }}>
                    <Table stickyHeader aria-label="sticky table">
                        <TableHead>
                            <TableRow sx={{ "& > th": { padding: "2px" } }}>
                                {columns.map((column) => (
                                    <TableCell
                                        key={column.id}
                                        align={column.align}
                                        sx={{
                                            backgroundColor: "#dddbff",
                                            color: "#333",
                                            fontWeight: "600",
                                            padding: "4px 2px 6px 5px !important",
                                            border: "0.5px solid #ddd",
                                            borderBottom: "2px solid #ddd",
                                            textAlign: "center",
                                            minWidth: column.minWidth || 'auto',
                                            width: column.width || 'auto',
                                            maxWidth: column.maxWidth || 'none',
                                        }}
                                    >
                                        <Typography
                                            variant="subtitle1"
                                            fontWeight="bold"
                                            sx={{ borderBottom: "0.5px solid #ddd", textAlign: 'start' }} // Table header alignment
                                        >
                                            {column.label}
                                        </Typography>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedRows.map((row, rowIndex) => {
                                const isBalanceBedRow = row['Total'] === 'Balance';

                                return (
                                    <TableRow
                                        hover
                                        tabIndex={-1}
                                        key={rowIndex}
                                        sx={{
                                            "& > td": {
                                                padding: "0px",
                                                border: "0.5px solid #ddd",
                                                backgroundColor: isBalanceBedRow ? '#e7cffe' : undefined,
                                                color: isBalanceBedRow ? '#4a0072' : undefined,
                                                fontWeight: isBalanceBedRow ? 'bold' : undefined,
                                            },
                                            "&:hover": {
                                                backgroundColor: isBalanceBedRow ? '#d0b9ff' : "#f1f1f1",
                                                cursor: "pointer",
                                            },
                                            transition: "background-color 0.3s ease",
                                        }}
                                    >
                                        {columns.map((column) => (
                                            <TableCell
                                                align={column.align}
                                                key={column.id}
                                                sx={{
                                                    textAlign: 'center',
                                                    verticalAlign: 'middle',
                                                    minWidth: column.minWidth || 'auto',
                                                    width: column.width || 'auto',
                                                    maxWidth: column.maxWidth || 'none',
                                                }}
                                            >
                                                {renderCell(row[column.id], column.id, isBalanceBedRow)}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>

                {isLoading && (
                    <Box sx={{
                        position: "absolute",
                        top: 100,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "rgba(255, 255, 255, 0.8)",
                        zIndex: 1,
                    }}>
                        <CircularProgress sx={{ color: "#635bff" }} />
                    </Box>
                )}

                {/* <TablePagination
                    rowsPerPageOptions={[15, 30, 50]}
                    component="div"
                    count={pagination.totalRecords}
                    rowsPerPage={pagination.pageSize}
                    page={page}
                    onPageChange={(event, newPage) => handleChangePage(newPage)}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    sx={{
                        backgroundColor: "#dddbff",
                        borderTop: "1px solid #ddd",
                        color: "#333",
                        borderRadius: "0 0 12px 12px",
                        "& .MuiToolbar-root": {
                            display: "flex",
                            justifyContent: "flex-end",
                            alignItems: "center",
                            width: "100%",
                            flexWrap: "wrap",
                        },
                        "& .MuiTablePagination-displayedRows": {
                            display: "none",
                        },
                        "& .MuiTablePagination-actions": {
                            display: "none",
                        },
                        "& .MuiTablePagination-selectLabel": {
                            marginBottom: 2,
                        },
                        "& .MuiTablePagination-select": {
                            marginBottom: 0,
                        },
                    }}
                    ActionsComponent={() => (
                        <Pagination
                            count={pagination.totalPages}
                            page={pagination.currentPage}
                            onChange={(event, newPage) => handleChangePage(newPage - 1)}
                            showFirstButton
                            showLastButton
                            siblingCount={1}
                            boundaryCount={1}
                            sx={{
                                "& .MuiPaginationItem-root.Mui-selected": {
                                    backgroundColor: "#635bff",
                                    color: "#ffffff",
                                },
                            }}
                        />
                    )}
                /> */}
            </Paper>
            {!isLoading && rows.length === 0 && (
                <Box sx={{
                    width: "100%",
                    margin: "20px auto",
                    display: "flex",
                    justifyContent: "center",
                    color: "red",
                    gap: 3
                }}>
                    No records available...
                    <span style={{ color: 'green' }}>Click the get data button to retrieve records.</span>
                </Box>
            )}
        </Box>
    );
};

export default TableComponent;